import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatSelectChange, MatCheckboxChange } from '@angular/material';
import { Airport } from '../models/airport.model';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { CountriesService } from '../../shared/services/countries.service';
import { Country } from '../../business/models/country.model';
import { State } from '../../business/models/state.model';
import { formatPhoneString, undoPhoneFormat } from '../../util/phone';
import { OverviewService } from '../service/overview.service';
import * as moment from 'moment-timezone';


@Component({
  selector: 'app-edit-header',
  templateUrl: './edit-island-overview.component.html',
  styleUrls: ['./edit-island-overview.component.scss'],
})

export class EditIslandOverviewComponent implements OnInit {
  isLoading = true;
  countrySelectId = '1';
  timezoneSelectId = '2';
  airport: Airport;
  formGroup: FormGroup;
  countries: Country[];
  timezones: string[];
  postalCodePlaceHolder = '';
  states: State [] = new Array<State>();
  statesMailing: State [] = new Array<State>();

  constructor(private matDialogRef: MatDialogRef<EditIslandOverviewComponent>,
              @Inject(MAT_DIALOG_DATA) data,
              private overviewService: OverviewService,
              private countriesService: CountriesService) {
    this.airport = data.airport; // data object defined in parent;
  }

  async ngOnInit() {
    // Defining form controls and validation.
    this.formGroup = new FormGroup({
      title: new FormControl(this.airport.name, [Validators.required, Validators.maxLength(50)]),
      icaoId: new FormControl(this.airport.icaoId, [Validators.maxLength(4)]),
      street1: new FormControl(this.airport.address1, [Validators.required, Validators.maxLength(80)]),
      street2: new FormControl(this.airport.address2, [Validators.maxLength(80)]),
      stateProvince: new FormControl(''),
      postalCode: new FormControl(this.airport.postalCode),
      city: new FormControl(this.airport.city, [Validators.required, Validators.maxLength(40)]),
      country: new FormControl('', [Validators.required]),
      phone: new FormControl(formatPhoneString(this.airport.mainPhone), [Validators.pattern('^([(]\\d{3}[)]\\s\\d{3}[-]\\d{4})$')]),
      webAddress: new FormControl(this.airport.website, [Validators.maxLength(100)]),
      email: new FormControl(this.airport.email, [Validators.maxLength(55), this.customEmailValidator]),
      timezone: new FormControl(this.airport.timeZone)
    });
    try {
      this.countries = await this.countriesService.getCountries();
      this.timezones = moment.tz.names();
    } catch (e) {
      this.close({cancelBtnClicked: false, success: false});
    }

    if (this.airport.country) {
      try {
        this.states = await this.countriesService.getStates(this.airport.country);
        this.states.length ? this.formGroup.controls['stateProvince'].setValidators([Validators.required, Validators.maxLength(50)]) :
          this.formGroup.controls['stateProvince'].setValidators([Validators.maxLength(30)]);
      } catch (e) {
        this.close({cancelBtnClicked: false, success: false});
      }
    }
    this.isLoading = false;
    this.formGroup.patchValue({country: this.airport.country || ''});
    this.formGroup.patchValue({stateProvince: this.airport.stateProvince || ''});
    this._setPostalCodeValidation(this.airport.country);
  }

  private customEmailValidator(control: AbstractControl): ValidationErrors {
    if (!control.value) {
      return null;
    }

    return Validators.email(control);
  }

  /**
   * triggered when value inside countries dropdown changes; event.value contains two digit country code.
   * @param {MatSelectChange} event
   * @return {Promise<void>}
   */
  async countryChange(event: MatSelectChange) {
    // if value property is empty return
    if (!event.value) { return; }
    await this._setAddressForm(event.value);
    if (event.value === this.airport.country) {
      this.formGroup.patchValue({stateProvince: this.airport.stateProvince || ''});
    }
  }

  /**
   * triggered when value inside timezone dropdown changes; event.value contains timezone string from moment.timezone.names.
   * @param {MatSelectChange} event
   * @return {Promise<void>}
   */
  timezoneChange(event: MatSelectChange) {
    // if value property is empty return
    if (!event.value) { return; }
    if (event.value === this.airport.timeZone) {
      this.formGroup.patchValue({timezone: this.airport.timeZone || ''});
    }
  }

  /**
   * changes states dropdown when a different country is chosen.
   * also changes validators depending on whether or states list exists for country
   * @param {string} value
   * @return {Promise<void>}
   * @private
   */
  async _setAddressForm(value: string) {
    this.isLoading = true;
    try {
      this.states = await this.countriesService.getStates(value);
    } catch (e) {
      this.close({cancelBtnClicked: false, success: false});
    }
    this.formGroup.patchValue({stateProvince: ''});
    this.isLoading = false;
    if (!this.states.length) {
      this.formGroup.controls['stateProvince'].setValidators([]);
    } else {
      this.formGroup.controls['stateProvince'].setValidators([Validators.required]);
    }
    this._setPostalCodeValidation(value);
  }

  /**
   * If countryCode is 'US', Postal Code field becomes required.
   * @param {string} countryCode
   * @private
   */
  _setPostalCodeValidation(countryCode: string) {
    if (countryCode === 'US') {
      this.postalCodePlaceHolder = 'Zip Code *';
      this.formGroup.controls['postalCode'].setValidators([
        Validators.required,
        Validators.pattern('^\\d{5}$')
      ]);
      this.formGroup.controls['postalCode'].updateValueAndValidity();
    } else {
      this.postalCodePlaceHolder = 'Postal Code';
      this.formGroup.controls['postalCode'].setValidators([Validators.maxLength(9)]);
      this.formGroup.controls['postalCode'].updateValueAndValidity();
    }
  }

  /**
   * formats passed in phone number string;
   * @param {string} field
   * @param {string} value
   * @return {string}
   */
  formatPhoneNumber (field: string, value: string): string {
    const NEW_NUMBER: string = formatPhoneString(value);
    this.formGroup.patchValue({[field]: NEW_NUMBER});
    return NEW_NUMBER;
  }

  /**
   * event triggered by keyup event on phone number or fax fields
   * @param event
   * @param {string} field
   * @param phoneInputObj
   */
  phoneFieldKeyUp (event, field: string, phoneInputObj) {
    const value: string = this.formGroup.get(field).value;
    const rawValue = undoPhoneFormat(value);
    const KEY = event.key;

    if (KEY === 'Backspace' && rawValue.length === 10 && phoneInputObj.selectionStart === rawValue.length) {
      this.formatPhoneNumber(field, value);
    } else if (phoneInputObj.selectionStart < value.length) {
    } else if (KEY === 'Backspace' || KEY === 'ArrowLeft' || KEY === 'ArrowRight') {
      return;
    } else {
      this.formatPhoneNumber(field, value);
    }
  }

  /**
   * triggered when phone number or fax field goes out of focus
   * @param {string} field
   */
  phoneFieldBlur(field: string) {
    const VALUE: string = this.formGroup.get(field).value;
    if (VALUE === '') {
      return;
    }
    this.formatPhoneNumber(field, VALUE);
  }


  /**
   * closes modal
   */
  close(result: { cancelBtnClicked: boolean, success: boolean, airport?: Airport } = { cancelBtnClicked: true, success: false }) {
    if (result) {
      this.matDialogRef.close(result);
    }
  }

  /**
   * submits form values to api server if formGroup.valid property is true
   * @return {Promise<void>}
   */
  async save() {
    const TEMP_AIRPORT = this.airport;
    if (!this.formGroup.valid) {
      return;
    }

    // form values are mapped to airport object before api call.
    this.airport.name = this.formGroup.value.title;
    this.airport.icaoId = this.formGroup.value.icaoId;
    // airport address
    this.airport.address1 = this.formGroup.value.street1;
    this.airport.address2 = this.formGroup.value.street2 || null;
    this.airport.stateProvince = this.formGroup.value.stateProvince || null;
    this.airport.postalCode = this.formGroup.value.postalCode || null;
    this.airport.city = this.formGroup.value.city;
    this.airport.country = this.formGroup.value.country || null;
    // contact info & web address
    this.airport.mainPhone = undoPhoneFormat(this.formGroup.value.phone);
    this.airport.website = this.formGroup.value.webAddress;
    this.airport.email = this.formGroup.value.email;
    this.airport.timeZone = this.formGroup.value.timezone;

    // make api call
    try {
      await this.overviewService.putIslandAirport(this.airport);
      this.close({cancelBtnClicked: false, success: true, airport: this.airport});
    } catch (e) {
      this.airport = TEMP_AIRPORT;
      this.close({cancelBtnClicked: false, success: false});
    }
  }
}
